import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";

import { errorHandling } from "../../../lib/apollo/utils";
import { useInsuranceCheckPoll } from "../../../lib/insurance/useInsuranceCheckPoll";
import { coverageCheckingStorage } from "../../../lib/storage";
import { useAppState } from "../../../lib/useAppState";
import { useCoveragePlanStore } from "../coveragePlanStore";
import { coverageInsuranceQuery } from "./queries";
import {
  type CheckingWait,
  type CoverageRead,
  readCoverage,
} from "./readCoverage";

const LONG_WAIT_MS = 60_000;
const STUCK_WAIT_MS = 10 * 60_000;
const SLOW_POLL_MS = 30_000;

export const useCoverage = () => {
  const { data, loading, error, refetch } = useQuery(coverageInsuranceQuery, {
    context: errorHandling("caller"),
  });
  const membershipId = useCoveragePlanStore(state => state.membershipId);
  // Null until the first answer arrives.
  const answer = useMemo(
    () => (data ? readCoverage(data.myInsurance, membershipId) : null),
    [data, membershipId],
  );
  const wait = useCheckingWait(
    answer ? answer.kind === "checking" : null,
    data?.myInsurance.checkStatus.lastRequestedAt ??
      data?.myInsurance.checkStatus.lastAttemptAt ??
      null,
  );
  // A check still running lands in place, however late.
  useInsuranceCheckPoll(coverageInsuranceQuery, {
    polling: answer?.kind === "checking",
    landed: d => readCoverage(d.myInsurance).kind !== "checking",
    intervalMs: wait === "usual" ? undefined : SLOW_POLL_MS,
  });
  const { isActive } = useAppState();
  const wasActive = useRef(isActive);
  useEffect(() => {
    if (isActive && !wasActive.current && wait !== "usual") {
      void refetch();
    }
    wasActive.current = isActive;
  }, [isActive, wait, refetch]);
  const read = useMemo(
    (): CoverageRead | null =>
      answer?.kind === "checking" ? { ...answer, wait } : answer,
    [answer, wait],
  );
  return { read, loading, error, refetch };
};

const SEEN_AT = "seenAt";

/** The first-seen time is one per device, so the next user starts afresh. */
export const clearCheckingWait = () => coverageCheckingStorage.remove(SEEN_AT);

/**
 * Timed from the last request or attempt, else from when the check was first
 * seen; both outlast a reload. `checking` is null until the first read.
 */
function useCheckingWait(
  checking: boolean | null,
  since: Date | null,
): CheckingWait {
  const [seenAt, setSeenAt] = useState<number | null>(null);
  const [, tick] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (checking === null) {
      return;
    }
    if (!checking) {
      clearCheckingWait();
      setSeenAt(null);
      return;
    }
    const stored = Number(coverageCheckingStorage.get(SEEN_AT));
    const at = stored > 0 ? stored : Date.now();
    coverageCheckingStorage.set(SEEN_AT, String(at));
    setSeenAt(at);
  }, [checking]);
  const start = !checking ? null : since ? since.getTime() : seenAt;
  const waited = start === null ? 0 : Date.now() - start;
  const wait: CheckingWait =
    waited >= STUCK_WAIT_MS
      ? "stuck"
      : waited >= LONG_WAIT_MS
        ? "long"
        : "usual";
  useEffect(() => {
    if (start === null || wait === "stuck") {
      return;
    }
    const next = start + (wait === "usual" ? LONG_WAIT_MS : STUCK_WAIT_MS);
    const timer = setTimeout(tick, next - Date.now());
    return () => clearTimeout(timer);
  }, [start, wait]);
  return wait;
}
